import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { InsuranceCompanyForm } from './InsuranceCompanyForm';
import type { InsuranceCompanyFormData } from './InsuranceCompanyForm';
import { useInsuranceCompanies } from '../hooks/useInsuranceCompanies';
import { useCreateInsuranceCompany } from '../hooks/useCreateInsuranceCompany';
import { useUpdateInsuranceCompany } from '../hooks/useUpdateInsuranceCompany';
import { useDeleteInsuranceCompany } from '../hooks/useDeleteInsuranceCompany';
import type {
  InsuranceCompanyDto,
  CreateInsuranceCompanyRequest,
  UpdateInsuranceCompanyRequest,
} from '../types/insurance';

export function InsuranceCompanyTable() {
  const [showInactive, setShowInactive] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<InsuranceCompanyDto | null>(null);
  const [deletingCompany, setDeletingCompany] = useState<InsuranceCompanyDto | null>(null);

  const {
    data: companies,
    isLoading,
    error: fetchError,
    refetch,
  } = useInsuranceCompanies({ includeInactive: showInactive });

  const {
    create,
    isLoading: isCreating,
    error: createError,
  } = useCreateInsuranceCompany(() => {
    setIsFormOpen(false);
    refetch();
  });

  const {
    update,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateInsuranceCompany(() => {
    setEditingCompany(null);
    setIsFormOpen(false);
    refetch();
  });

  const {
    deleteCompany,
    isLoading: isDeleting,
    error: deleteError,
  } = useDeleteInsuranceCompany(() => {
    setDeletingCompany(null);
    refetch();
  });

  const handleCreate = useCallback(
    async (data: InsuranceCompanyFormData) => {
      const request: CreateInsuranceCompanyRequest = {
        name: data.name,
        address1: data.address1 || null,
        address2: data.address2 || null,
        city: data.city || null,
        state: data.state || null,
        zip: data.zip || null,
        phone: data.phone || null,
        email: data.email || null,
      };
      await create(request);
    },
    [create]
  );

  const handleUpdate = useCallback(
    async (data: InsuranceCompanyFormData) => {
      if (!editingCompany) return;
      const request: UpdateInsuranceCompanyRequest = {
        name: data.name,
        address1: data.address1 || null,
        address2: data.address2 || null,
        city: data.city || null,
        state: data.state || null,
        zip: data.zip || null,
        phone: data.phone || null,
        email: data.email || null,
        isActive: data.isActive,
      };
      await update(editingCompany.id, request);
    },
    [editingCompany, update]
  );

  const handleDelete = useCallback(async () => {
    if (!deletingCompany) return;
    try {
      await deleteCompany(deletingCompany.id);
    } catch {
      // Error handled by hook
    }
  }, [deletingCompany, deleteCompany]);

  const handleOpenCreate = () => {
    setEditingCompany(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (company: InsuranceCompanyDto) => {
    setEditingCompany(company);
    setIsFormOpen(true);
  };

  const handleFormClose = (open: boolean) => {
    if (!open) {
      setIsFormOpen(false);
      setEditingCompany(null);
    }
  };

  const error = fetchError || createError || updateError || deleteError;

  const formatLocation = (company: InsuranceCompanyDto): string => {
    const parts = [company.city, company.state].filter(Boolean);
    return parts.join(', ');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Insurance Companies</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <LoadingSpinner label="Loading insurance companies..." />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Insurance Companies</CardTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-inactive-insurance"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                <Label
                  htmlFor="show-inactive-insurance"
                  className="text-sm font-normal cursor-pointer"
                >
                  Show inactive
                </Label>
              </div>
              <Button onClick={handleOpenCreate} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Company
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ErrorAlert error={error} className="mb-4" />

          {companies.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No insurance companies found. Click &quot;Add Company&quot; to create one.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company Name</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {companies.map((company) => (
                  <TableRow key={company.id}>
                    <TableCell>
                      <span className={!company.isActive ? 'text-muted-foreground' : ''}>
                        {company.name}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatLocation(company) || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {company.phone || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {company.email || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={company.isActive ? 'default' : 'secondary'}
                        className={
                          company.isActive
                            ? 'bg-green-100 text-green-800 hover:bg-green-100'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-100'
                        }
                      >
                        {company.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(company)}
                          title="Edit company"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {company.isActive && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            title="Deactivate company"
                            onClick={() => setDeletingCompany(company)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <InsuranceCompanyForm
        company={editingCompany}
        open={isFormOpen}
        onOpenChange={handleFormClose}
        onSubmit={editingCompany ? handleUpdate : handleCreate}
        isSubmitting={isCreating || isUpdating}
        error={editingCompany ? updateError : createError}
      />

      <AlertDialog
        open={!!deletingCompany}
        onOpenChange={(open) => !open && setDeletingCompany(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Deactivate &quot;{deletingCompany?.name}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the insurance company. It will no longer appear in dropdown
              menus but existing leads referencing this company will be preserved. You can
              reactivate it later by enabling &quot;Show inactive&quot; and editing the company.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deactivating...' : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
